import { getTagValue } from "applesauce-core/helpers";

/**
 * Helper function to get matching tags from AppleSauce events
 * @param {Object} release - The Nostr event containing release information
 * @param {string} tagName - The tag name to search for
 * @returns {Array} Array of matching tags
 */
const getMatchingTags = (release, tagName) => {
  if (typeof release.getMatchingTags === 'function') {
    return release.getMatchingTags(tagName);
  }
  // AppleSauce format: tags is an array of [tagName, value, ...] arrays
  return release.tags?.filter(tag => tag[0] === tagName) || [];
};

/**
 * Get a display-friendly version number from a Nostr event
 * @param {Object} release - The Nostr event containing release information
 * @returns {string} A formatted version string
 */
export const getReleaseVersion = (release) => {
  // 'v' is the short single-letter tag (new standard); 'version' is
  // the long-form alias older events may carry.
  return (
    getTagValue(release, "v") ||
    getMatchingTags(release, "version")?.[0]?.[1] ||
    "Unknown"
  );
};

/**
 * Get the formatted release date for a release
 * @param {Object} release - The Nostr event containing release information
 * @returns {string} A formatted date string
 */
export const getReleaseDate = (release) => {
  if (!release.created_at) return "Unknown";
  
  const date = new Date(release.created_at * 1000);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Get the formatted release date and time for a release
 * @param {Object} release - The Nostr event containing release information
 * @returns {string} A formatted date and time string
 */
export const getReleaseDateWithTime = (release) => {
  if (!release.created_at) return "Unknown";
  
  const date = new Date(release.created_at * 1000);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Get the release channel from a release event
 * @param {Object} release - The Nostr event containing release information
 * @returns {string} The release channel
 */
export const getReleaseChannel = (release) => {
  // 'c' is the short single-letter tag; 'release_channel' is the
  // long-form alias older events may carry.
  return (
    getMatchingTags(release, "c")?.[0]?.[1] ||
    getMatchingTags(release, "release_channel")?.[0]?.[1] ||
    "unknown"
  );
};

/**
 * Get architecture details for a release
 * @param {Object} release - The Nostr event containing release information 
 * @returns {string} The architecture or "Unknown"
 */
export const getReleaseArchitecture = (release) => {
  // 'A' is the short single-letter tag our pipeline emits;
  // 'architecture' is the long-form alias older events carry.
  return (
    getMatchingTags(release, "A")?.[0]?.[1] ||
    getMatchingTags(release, "architecture")?.[0]?.[1] ||
    "Unknown"
  );
};

/**
 * Get compression type for a release
 * @param {Object} release - The Nostr event containing release information
 * @returns {string} The compression type or "none"
 */
export const getReleaseCompression = (release) => {
  return getMatchingTags(release, "compression")?.[0]?.[1] || "none";
};

/**
 * Get the package format (ipk / apk) for a release. Checks the 'format'
 * tag first; falls back to inferring from the filename/URL extension.
 * @param {Object} release - The Nostr event containing release information
 * @returns {string} 'ipk', 'apk', or 'unknown'
 */
export const getReleaseFormat = (release) => {
  const tag = getMatchingTags(release, "format")?.[0]?.[1];
  if (tag === "ipk" || tag === "apk") return tag;

  const filename = getMatchingTags(release, "filename")?.[0]?.[1] || "";
  if (filename.endsWith(".ipk")) return "ipk";
  if (filename.endsWith(".apk")) return "apk";

  const url = getMatchingTags(release, "url")?.[0]?.[1] || "";
  if (url.endsWith(".ipk")) return "ipk";
  if (url.endsWith(".apk")) return "apk";

  return "unknown";
};

/**
 * Get OpenWRT version details
 * @param {Object} release - The Nostr event containing release information
 * @returns {string} The OpenWRT version or "Unknown"
 */
export const getReleaseOpenWrtVersion = (release) => {
  return getMatchingTags(release, "openwrt_version")?.[0]?.[1] || "Unknown";
};

/**
 * Get device ID for a release
 * @param {Object} release - The Nostr event containing release information
 * @returns {string} The device ID or "Unknown"
 */
export const getReleaseDeviceId = (release) => {
  // 'd' is the short single-letter tag OS events emit alongside the
  // legacy 'device_id' long form; read short first for consistency
  // with the other helpers and to survive a future long-form cleanup.
  return (
    getMatchingTags(release, "d")?.[0]?.[1] ||
    getMatchingTags(release, "device_id")?.[0]?.[1] ||
    "Unknown"
  );
};

/**
 * Get supported devices for a release
 * @param {Object} release - The Nostr event containing release information
 * @returns {string} The supported devices or "Unknown"
 */
export const getReleaseSupportedDevices = (release) => {
  return getMatchingTags(release, "supported_devices")?.[0]?.[1] || "Unknown";
};

/**
 * Get the download URL for a release
 * @param {Object} release - The Nostr event containing release information
 * @returns {string} The download URL or null
 */
export const getReleaseDownloadUrl = (release) => {
  return getMatchingTags(release, "url")?.[0]?.[1] || null;
};

/**
 * Get the filename for a release, with the correct file extension.
 * Blossom-hosted files use the SHA-256 hash as the URL path segment with no
 * extension; this helper keeps that hash-as-filename convention and just
 * appends the format/firmware extension so wget/opkg find the file by name.
 * @param {Object} release - The Nostr event containing release information
 * @returns {string|null} Filename with extension, or null if no URL is available
 */
export const getReleaseFilename = (release) => {
  const url = getReleaseDownloadUrl(release);
  if (!url) return null;

  const basename = url.split("/").pop();
  if (basename.includes(".")) return basename;

  const format = getReleaseFormat(release);
  if (format === "ipk" || format === "apk") return `${basename}.${format}`;

  return `${basename}.bin`;
};

/**
 * Get the file hash for verification
 * @param {Object} release - The Nostr event containing release information
 * @returns {string} The file hash or null
 */
export const getReleaseFileHash = (release) => {
  return getMatchingTags(release, "x")?.[0]?.[1] ||
         getMatchingTags(release, "ox")?.[0]?.[1] || null;
};

/**
 * Get the MIME type of the release file
 * @param {Object} release - The Nostr event containing release information
 * @returns {string} The MIME type or "application/octet-stream"
 */
export const getReleaseMimeType = (release) => {
  return getMatchingTags(release, "m")?.[0]?.[1] || "application/octet-stream";
};

/**
 * Determine the product type from a release
 * @param {Object} release - The Nostr event containing release information
 * @returns {string} Either 'tollgate-os', 'tollgate-wrt', or 'tollgate-module-basic-go'
 */
export const getReleaseProductType = (release) => {
  // Check name tags in order of specificity. 'n' is the short-form tag
  // our pipeline emits (NIP-94 single-letter alias). 'name' and
  // 'package_name' are the older long-form equivalents some events
  // still carry; keep reading them for backward compatibility.
  const nameTags = [
    getMatchingTags(release, "n")?.[0]?.[1],
    getMatchingTags(release, "name")?.[0]?.[1],
    getMatchingTags(release, "package_name")?.[0]?.[1],
  ].filter(Boolean);

  for (const name of nameTags) {
    if (name.includes('tollgate-os')) return 'tollgate-os';
    if (name.includes('tollgate-wrt')) return 'tollgate-wrt';
    if (name.includes('tollgate-module-basic-go')) return 'tollgate-module-basic-go';
  }
  
  // Check if it has deprecated version tags
  if (getMatchingTags(release, "tollgate_os_version")?.[0]?.[1]) {
    return 'tollgate-os';
  }

  
  // Fallback: check content, filename, or URL
  const content = release.content?.toLowerCase() || '';
  const url = getReleaseDownloadUrl(release)?.toLowerCase() || '';
  const filename = getMatchingTags(release, "filename")?.[0]?.[1]?.toLowerCase() || '';
  
  if (content.includes('basic') || url.includes('basic') || filename.includes('basic') ||
      content.includes('module') || url.includes('module') || filename.includes('module')) {
    return 'tollgate-module-basic-go';
  }
  
  if (content.includes('core') || url.includes('core') || filename.includes('core')) {
    return 'tollgate-wrt';
  }
  
  // Default to OS
  return 'tollgate-os';
};

/**
 * Get a human-readable product name
 * @param {string} productType - The product type ('tollgate-os', 'tollgate-wrt', or 'tollgate-module-basic-go')
 * @returns {string} Human-readable product name
 */
export const getProductDisplayName = (productType) => {
  switch (productType) {
    case 'tollgate-os':
      return 'TollGate OS';
    case 'tollgate-wrt':
      return 'TollGate WRT';
    case 'tollgate-module-basic-go':
      return 'TollGate Basic Module';
    default:
      return 'TollGate';
  }
};

/**
 * Filter releases based on criteria
 * @param {Array} releases - Array of release events
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered releases
 */
export const filterReleases = (releases, filters) => {
  if (!releases || !Array.isArray(releases)) return [];
  
  return releases.filter(release => {
    // Filter by channels
    if (filters.channels && filters.channels.length > 0) {
      const channel = getReleaseChannel(release);
      if (!filters.channels.includes(channel)) return false;
    }
    
    // Filter by product types
    if (filters.products && filters.products.length > 0) {
      const productType = getReleaseProductType(release);
      if (!filters.products.includes(productType)) return false;
    }
    
    return true;
  });
};

/**
 * Deduplicate releases by version only, keeping the newest release for each version
 * This shows one release per version regardless of product type or architecture
 * @param {Array} releases - Array of release events
 * @returns {Array} Deduplicated releases (sorted by created_at, newest first)
 */
export const deduplicateReleases = (releases) => {
  if (!releases || !Array.isArray(releases)) return [];
  
  // First, sort by created_at (newest first) to ensure we process newest releases first
  const sortedReleases = [...releases].sort((a, b) => {
    const aTime = a.created_at || 0;
    const bTime = b.created_at || 0;
    return bTime - aTime; // Newest first
  });
  
  const releaseMap = new Map();
  
  sortedReleases.forEach(release => {
    const version = getReleaseVersion(release);
    
    // Only keep the first occurrence (which is the newest due to sorting)
    if (!releaseMap.has(version)) {
      releaseMap.set(version, release);
    }
  });
  
  return Array.from(releaseMap.values());
};

/**
 * Sort releases by creation date (newest first)
 * @param {Array} releases - Array of release events
 * @returns {Array} Sorted releases
 */
export const sortReleasesByDate = (releases) => {
  if (!releases || !Array.isArray(releases)) return [];
  
  return [...releases].sort((a, b) => {
    const aTime = a.created_at || 0;
    const bTime = b.created_at || 0;
    return bTime - aTime; // Newest first
  });
};

/**
 * Get unique values from releases for filter options
 * @param {Array} releases - Array of release events
 * @param {string} field - Field to extract unique values from
 * @returns {Array} Array of unique values
 */
export const getUniqueReleaseValues = (releases, field) => {
  if (!releases || !Array.isArray(releases)) return [];
  
  const values = new Set();
  
  releases.forEach(release => {
    let value;
    switch (field) {
      case 'channels':
        value = getReleaseChannel(release);
        break;
      case 'products':
        value = getReleaseProductType(release);
        break;
      default:
        return;
    }
    
    if (value && value !== 'Unknown') {
      values.add(value);
    }
  });
  
  return Array.from(values).sort();
};

/**
 * Check if a release is a development release
 * @param {Object} release - The Nostr event containing release information
 * @returns {boolean} True if this is a dev release
 */
export const isDevRelease = (release) => {
  const channel = getReleaseChannel(release);
  return channel === 'dev';
};

/**
 * Check if a release is a pre-release (beta, alpha, or dev)
 * @param {Object} release - The Nostr event containing release information
 * @returns {boolean} True if this is a pre-release
 */
export const isPreRelease = (release) => {
  const channel = getReleaseChannel(release);
  return ['beta', 'alpha', 'dev'].includes(channel);
};

/**
 * Count releases excluding dev releases
 * @param {Array} releases - Array of release events
 * @returns {Object} Object with stable and prerelease counts
 */
export const countReleases = (releases) => {
  if (!releases || !Array.isArray(releases)) {
    return { total: 0, stable: 0, prerelease: 0 };
  }
  
  const nonDevReleases = releases.filter(release => !isDevRelease(release));
  const stableReleases = nonDevReleases.filter(release => !isPreRelease(release));
  const prereleases = nonDevReleases.filter(release => isPreRelease(release));
  
  return {
    total: nonDevReleases.length,
    stable: stableReleases.length,
    prerelease: prereleases.length
  };
};

/**
 * Get display text for release count
 * @param {Array} releases - Array of release events
 * @returns {string} Formatted release count text
 */
export const getReleaseCountText = (releases) => {
  const counts = countReleases(releases);
  
  if (counts.total === 0) {
    return '0 releases';
  }
  
  if (counts.prerelease === 0) {
    return `${counts.total} releases`;
  }
  
  return `${counts.total} releases ,${counts.prerelease} pre`;
};

/**
 * Find all releases with the same version and product type but different architectures/devices
 * Returns ALL matching releases without deduplication - let grouping functions handle that
 * @param {Array} releases - Array of all release events
 * @param {Object} currentRelease - The current release to find alternatives for
 * @returns {Array} Array of alternative releases (not deduplicated)
 */
export const findAlternativeReleases = (releases, currentRelease) => {
  if (!releases || !Array.isArray(releases) || !currentRelease) return [];
  
  const currentVersion = getReleaseVersion(currentRelease);
  const currentProductType = getReleaseProductType(currentRelease);
  const currentArchitecture = getReleaseArchitecture(currentRelease);
  const currentDeviceId = getReleaseDeviceId(currentRelease);
  const currentCompression = getReleaseCompression(currentRelease);
  
  // Filter to matching version and product type (excluding current release)
  return releases.filter(release => {
    if (release.id === currentRelease.id) return false; // Exclude current release
    
    const version = getReleaseVersion(release);
    const productType = getReleaseProductType(release);
    
    // Must match version and product type
    if (version !== currentVersion || productType !== currentProductType) {
      return false;
    }
    
    // For OS releases, show different device_ids OR same device with different compression
    if (productType === 'tollgate-os') {
      const deviceId = getReleaseDeviceId(release);
      const compression = getReleaseCompression(release);
      // Include if different device, OR same device but different compression
      return deviceId !== currentDeviceId || compression !== currentCompression;
    }
    
    // For packages, show different architectures OR same architecture
    // with different compression OR different format (ipk vs apk).
    const architecture = getReleaseArchitecture(release);
    const compression = getReleaseCompression(release);
    const format = getReleaseFormat(release);
    const currentFormat = getReleaseFormat(currentRelease);
    return (
      architecture !== currentArchitecture ||
      compression !== currentCompression ||
      format !== currentFormat
    );
  });
};

/**
 * Group releases by (device, OpenWrt version), with compression variants per group.
 * One card per device-version pair so users can see and pick a specific OS
 * version without a separate top-level filter.
 * @param {Array} releases - Array of release events
 * @returns {Array} Array of {deviceId, openwrtVersion, displayName, compressionVariants}
 */
export const groupReleasesByDevice = (releases) => {
  if (!releases || !Array.isArray(releases)) return [];

  const groupMap = new Map();

  releases.forEach(release => {
    const deviceId = getReleaseDeviceId(release);
    const openwrtVersion = getReleaseOpenWrtVersion(release);
    const compression = getReleaseCompression(release);
    const key = `${deviceId}::${openwrtVersion}`;

    if (!groupMap.has(key)) {
      groupMap.set(key, {
        deviceId,
        openwrtVersion,
        displayName: openwrtVersion && openwrtVersion !== 'Unknown'
          ? `${deviceId} (OpenWrt ${openwrtVersion})`
          : deviceId,
        compressionVariants: new Map()
      });
    }

    const group = groupMap.get(key);

    if (!group.compressionVariants.has(compression) ||
        release.created_at > group.compressionVariants.get(compression).created_at) {
      group.compressionVariants.set(compression, release);
    }
  });

  // Compare OpenWrt versions numerically by segment so 25.12.2 > 24.10.4.
  const compareVersionDesc = (a, b) => {
    const parse = (s) => (s || '').split(/[^0-9]+/).map((x) => parseInt(x, 10) || 0);
    const sa = parse(a);
    const sb = parse(b);
    for (let i = 0; i < Math.max(sa.length, sb.length); i += 1) {
      const da = sa[i] || 0;
      const db = sb[i] || 0;
      if (da !== db) return db - da;
    }
    return 0;
  };

  // For each device, find the highest known OpenWrt version — that's the
  // one we mark as "recommended" when the device has multiple versions.
  const newestVersionByDevice = new Map();
  for (const group of groupMap.values()) {
    const v = group.openwrtVersion;
    if (!v || v === 'Unknown') continue;
    const current = newestVersionByDevice.get(group.deviceId);
    if (!current || compareVersionDesc(v, current) < 0) {
      newestVersionByDevice.set(group.deviceId, v);
    }
  }
  const versionCountByDevice = new Map();
  for (const group of groupMap.values()) {
    versionCountByDevice.set(
      group.deviceId,
      (versionCountByDevice.get(group.deviceId) || 0) + 1
    );
  }

  return Array.from(groupMap.values())
    .map(group => ({
      deviceId: group.deviceId,
      openwrtVersion: group.openwrtVersion,
      displayName: group.displayName,
      isRecommended:
        (versionCountByDevice.get(group.deviceId) || 0) > 1 &&
        newestVersionByDevice.get(group.deviceId) === group.openwrtVersion,
      compressionVariants: Array.from(group.compressionVariants.entries())
        .map(([compression, release]) => ({ compression, release }))
        .sort((a, b) => {
          if (a.compression === 'none') return -1;
          if (b.compression === 'none') return 1;
          return a.compression.localeCompare(b.compression);
        })
    }))
    // Same device's versions cluster together, newer OpenWrt first.
    .sort((a, b) => {
      const byDevice = a.deviceId.localeCompare(b.deviceId);
      if (byDevice !== 0) return byDevice;
      return compareVersionDesc(a.openwrtVersion, b.openwrtVersion);
    });
};

/**
 * Group releases by architecture, with compression variants for each architecture
 * @param {Array} releases - Array of release events
 * @returns {Array} Array of architecture groups with compression variants
 */
export const groupReleasesByArchitecture = (releases) => {
  if (!releases || !Array.isArray(releases)) return [];
  
  const architectureMap = new Map();
  
  releases.forEach(release => {
    const architecture = getReleaseArchitecture(release);
    const compression = getReleaseCompression(release);
    const format = getReleaseFormat(release);
    // Key compressionVariants by "<format>:<compression>" so ipk + apk
    // with the same compression coexist instead of overwriting each other.
    const key = `${format}:${compression}`;

    if (!architectureMap.has(architecture)) {
      architectureMap.set(architecture, {
        architecture,
        compressionVariants: new Map()
      });
    }

    const archGroup = architectureMap.get(architecture);

    if (!archGroup.compressionVariants.has(key) ||
        release.created_at > archGroup.compressionVariants.get(key).created_at) {
      archGroup.compressionVariants.set(key, release);
    }
  });

  // Convert Maps to arrays and sort variants (none first, then alpha).
  const result = Array.from(architectureMap.values()).map(group => ({
    architecture: group.architecture,
    compressionVariants: Array.from(group.compressionVariants.values())
      .map(release => ({
        compression: getReleaseCompression(release),
        format: getReleaseFormat(release),
        release
      }))
      .sort((a, b) => {
        if (a.compression === 'none' && b.compression !== 'none') return -1;
        if (b.compression === 'none' && a.compression !== 'none') return 1;
        const byComp = a.compression.localeCompare(b.compression);
        if (byComp !== 0) return byComp;
        return a.format.localeCompare(b.format);
      })
  }));

  return result;
};

/**
 * Truncate text to a specified maximum length, adding ellipsis if needed
 * @param {string} text - The text to truncate
 * @param {number} maxLength - The maximum allowed length (including ellipsis)
 * @returns {string} The truncated text with ellipsis if necessary
 */
export const truncateText = (text, maxLength) => {
  if (!text || text.length <= maxLength) return text || '';
  return text.substring(0, maxLength - 3) + '...';
};